<script setup lang="ts">
import type { FederationNewsInstitution, FederationNewsItem, FederationNewsTarget } from '@/components/federation/FederationNewsForm.vue';
import KgrPaginator from '@legacy/components/common/Paginator.vue';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { CalendarCheck, CalendarClock, CalendarX, Pencil, Plus, Star, Trash2 } from 'lucide';
import { computed, onMounted, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphIcon } from '@/components/base/icon';
import FederationNewsForm from '@/components/federation/FederationNewsForm.vue';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useDialog } from '@/plugins/dialog';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    target: FederationNewsTarget
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();
const { dialog } = useDialog();

const loading = ref(true);
const institutions = ref<FederationNewsInstitution[]>([]);
const newsList = ref<FederationNewsItem[]>([]);
const PAGE_SIZE = 20;
interface Pagination { limit: number, offset: number }
const pagination = ref<Pagination>({ limit: PAGE_SIZE, offset: 0 });
const totalCount = ref(0);
const mode = ref<'list' | 'create' | 'edit'>('list');
const editing = ref<FederationNewsItem | null>(null);

const base = computed(() => `/federations/${props.federationId}/news`);
const showPagination = computed(() => totalCount.value > PAGE_SIZE || pagination.value.offset > 0);

async function loadInstitutions() {
    if (props.target === 'clubs') {
        const response = await axios.get(`/federation/federations/${props.federationId}/clubs`);
        institutions.value = response.data.data.map((d: any) => ({ id: d.id, name: d.name }));
    } else if (props.target === 'kindergartens') {
        const response = await axios.get(`/federations/${props.federationId}/kindergartens`);
        institutions.value = response.data.data.map((d: any) => ({ id: d.id, name: d.name }));
    }
}

async function loadNews() {
    loading.value = true;
    try {
        const page = Math.floor(pagination.value.offset / pagination.value.limit) + 1;
        const response = await axios.get(base.value, { params: { target: props.target, page } });
        newsList.value = response.data.data;
        totalCount.value = response.data.meta?.pagination?.total ?? 0;
    } catch {
        notify.error('error.loadingData');
    } finally {
        loading.value = false;
    }
}

function onPaginate(settings: Pagination) {
    pagination.value = settings;
    loadNews();
}

function startCreate() {
    editing.value = null;
    mode.value = 'create';
}

function startEdit(news: FederationNewsItem) {
    editing.value = news;
    mode.value = 'edit';
}

function backToList() {
    mode.value = 'list';
    editing.value = null;
}

function saved() {
    notify.success(i18n.t('Neuigkeit wurde gespeichert.'));
    backToList();
    loadNews();
}

async function remove(news: FederationNewsItem) {
    const confirmed = await dialog.confirm({
        title: i18n.t('Neuigkeit löschen'),
        message: news.target === 'clubs'
            ? i18n.t('Soll die Neuigkeit „{title}“ samt ihrer Kopien in allen Vereinen gelöscht werden?', { title: news.title })
            : i18n.t('Soll die Neuigkeit „{title}“ samt ihrer Kopien in allen Einrichtungen gelöscht werden?', { title: news.title }),
        confirmButtonLabel: i18n.t('Löschen'),
        destructive: true,
    });
    if (!confirmed) {
        return;
    }
    try {
        await axios.delete(`${base.value}/${news.id}`);
        notify.success(i18n.t('Neuigkeit wurde gelöscht.'));
        loadNews();
    } catch (err: any) {
        notify.error(err.response?.data?.error?.message ?? i18n.t('Ein Fehler ist aufgetreten.'));
    }
}

async function patchTopNews(news: FederationNewsItem) {
    try {
        await axios.patch(`${base.value}/${news.id}/top-news`, { topNews: !news.topNews });
        news.topNews = !news.topNews;
        notify.success(i18n.t('Neuigkeit wurde aktualisiert'));
    } catch {
        notify.error(i18n.t('Neuigkeit konnte nicht aktualisiert werden'));
    }
}

function imageUrl(news: FederationNewsItem): string {
    return `/backend/thumbnail/federationNews/${news.id}/news_list_thumb/${news.image}`;
}

function formatDateTime(value: string | null): string {
    return value ? format(parseISO(value), 'dd.MM.yyyy HH:mm') : '';
}

function audienceText(news: FederationNewsItem): string {
    if (news.audience === 'staff') {
        return i18n.t('Nur Mitarbeiter');
    }
    if (news.target === 'clubs') {
        return i18n.t('Mitarbeiter, Mitglieder und Gäste');
    }

    return news.audience === 'public' ? i18n.t('Mitarbeiter, Eltern und Website') : i18n.t('Mitarbeiter und Eltern');
}

function metaText(news: FederationNewsItem): string {
    if (news.target === 'clubs') {
        return `${audienceText(news)}, ${i18n.t('Vereine')}: ${news.clubs.length}`;
    }

    return `${audienceText(news)}, ${i18n.t('Einrichtungen')}: ${news.kindergartens.length}`;
}

function institutionNames(news: FederationNewsItem): string {
    return (news.target === 'clubs' ? news.clubs : news.kindergartens).map((i) => i.name).join(', ');
}

function calendarIcon(news: FederationNewsItem): { icon: typeof CalendarCheck, title: string } {
    const now = new Date();
    const publishDate = news.publishDate ? parseISO(news.publishDate) : null;
    if (publishDate && publishDate > now) {
        return { icon: CalendarClock, title: i18n.t('Neuigkeit wird veröffentlicht am {date}', { date: publishDate.toLocaleString() }) };
    }
    const publishedEnd = news.publishedEnd ? parseISO(news.publishedEnd) : null;
    if (publishedEnd && publishedEnd < new Date(now.toDateString())) {
        return { icon: CalendarX, title: i18n.t('Neuigkeit ist nicht mehr veröffentlicht') };
    }
    if (publishedEnd) {
        return { icon: CalendarCheck, title: i18n.t('Veröffentlichungsdatum endet am {date}', { date: publishedEnd.toLocaleDateString() }) };
    }

    return { icon: CalendarCheck, title: i18n.t('Neuigkeit ist veröffentlicht') };
}

onMounted(async () => {
    try {
        await loadInstitutions();
    } catch {
        notify.error('error.loadingData');
    }
    await loadNews();
});
</script>

<template>
    <div>
        <template v-if="mode === 'list'">
            <div class="kgr-row kgr-postfix kgr-postfix--between items-end">
                <h2 class="kgr-headline kgr-headline--h2">
                    {{ i18n.t('Neuigkeiten') }}
                </h2>
                <SphButton
                    :icon="Plus"
                    :label="i18n.t('News.addNew')"
                    variant="primary"
                    @click="startCreate"
                />
            </div>

            <div
                v-if="loading"
                class="kgr-loading"
            ></div>

            <div
                v-else-if="newsList.length === 0"
                class="kgr-alert kgr-alert--info"
            >
                {{ i18n.t('Es wurden noch keine Neuigkeiten erstellt.') }}
            </div>

            <div
                v-else
                class="kgr-row"
            >
                <div class="kgr-grid kgr-grid--inline kgr-grid--gutters kgr-grid--bigGutters kgr-row">
                    <div
                        v-for="news in newsList"
                        :key="news.id"
                        class="kgr-grid__cell"
                        style="width: 350px"
                    >
                        <article class="kgr-card kgr-backgroundColor kgr-backgroundColor--white">
                            <div
                                v-if="news.image"
                                class="kgr-card__image kgr-card__image--full kgr-card__image--contain kgr-card__image--95to64"
                                :style="`background-image: url(${imageUrl(news)});`"
                            ></div>
                            <div
                                class="kgr-card__subtitle"
                                :title="institutionNames(news)"
                            >
                                {{ metaText(news) }}
                            </div>

                            <h3 class="kgr-card__name">
                                <a
                                    href="#"
                                    @click.prevent="startEdit(news)"
                                >
                                    {{ news.title }}
                                </a>
                            </h3>

                            <div class="kgr-card__contents">
                                {{ news.teaser }}
                            </div>

                            <div class="kgr-card__footer">
                                <div class="kgr-card__footerContents kgr-postfix kgr-postfix--center kgr-postfix--between">
                                    <div class="kgr-postfix__fluid">
                                        {{ formatDateTime(news.publishDate) }}
                                    </div>
                                    <div class="kgr-postfix__fixed flex items-center gap-2">
                                        <SphIcon
                                            :key="news.id + String(news.topNews)"
                                            size="md"
                                            :icon="Star"
                                            :class="{ 'top-news-active': news.topNews }"
                                            :title="news.topNews ? i18n.t('Als Top-News entfernen') : i18n.t('Als Top-News markieren')"
                                            style="cursor: pointer"
                                            @click="patchTopNews(news)"
                                        />
                                        <SphIcon
                                            size="md"
                                            :icon="calendarIcon(news).icon"
                                            :title="calendarIcon(news).title"
                                        />
                                        <SphButton
                                            :icon="Pencil"
                                            :title="i18n.t('News.modifyEntry')"
                                            variant="ghost"
                                            size="sm"
                                            @click="startEdit(news)"
                                        />
                                        <SphButton
                                            :icon="Trash2"
                                            :title="i18n.t('delete')"
                                            variant="ghost"
                                            size="sm"
                                            @click="remove(news)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
                <div class="kgr-row kgr-text--centered">
                    <KgrPaginator
                        v-if="showPagination"
                        :value="pagination"
                        :length="totalCount"
                        :show-limit-options="false"
                        show-pages
                        @input="onPaginate"
                    />
                </div>
            </div>
        </template>

        <template v-else>
            <h2 class="kgr-headline kgr-headline--h2 kgr-row">
                {{ mode === 'edit' ? i18n.t('News.modifyEntry') : i18n.t('News.addNew') }}
            </h2>
            <FederationNewsForm
                :federation-id="federationId"
                :target="target"
                :institutions="institutions"
                :news="editing"
                @cancel="backToList"
                @saved="saved"
            />
        </template>
    </div>
</template>

<style scoped>
.top-news-active :deep(path) {
    fill: currentColor;
}
</style>
